class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.currentPage = parseInt(this.getAttribute('data-current-page')) || 1;
    this.totalPages = parseInt(this.getAttribute('data-total-pages')) || 1;
    this.loading = false;
    this.sectionId = this.getAttribute('data-section-id');
    this.collectionUrl = this.getAttribute('data-collection-url');
    
    // Get current URL parameters for filtering/sorting
    this.urlParams = new URLSearchParams(window.location.search);
    
    // console.log('Infinite Scroll initialized:', {
    //   currentPage: this.currentPage,
    //   totalPages: this.totalPages,
    //   sectionId: this.sectionId,
    //   collectionUrl: this.collectionUrl,
    //   element: this,
    //   isConnected: this.isConnected
    // });
    
    // Wait for element to be fully connected to DOM
    if (this.isConnected) {
      this.init();
    } else {
      this.addEventListener('DOMContentLoaded', () => this.init());
    }
  }
  
  init() {
    console.log('Initializing infinite scroll...');
    this.setupIntersectionObserver();
    this.setupScrollListener(); // Add scroll listener as backup
    this.setupLoadMoreButton();
  }
  
  setupScrollListener() {
    // Add scroll listener as backup to intersection observer
    this.scrollHandler = () => {
      // Check if this component is still connected to the DOM
      if (!this.isConnected) {
        return;
      }
      
      if (this.loading || this.currentPage >= this.totalPages) return;
      
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Check if the infinite scroll element is near the viewport
      if (rect.top <= viewportHeight + 100) {
        console.log('Scroll threshold reached, loading more products');
        this.loadMoreProducts();
      }
    };
    
    // Throttle scroll events
    let ticking = false;
    this.throttledScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.scrollHandler();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', this.throttledScrollHandler, { passive: true });
  }
  
  disconnectedCallback() {
    // Clean up when component is removed
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.throttledScrollHandler) {
      window.removeEventListener('scroll', this.throttledScrollHandler);
    }
  }
  
  connectedCallback() {
    console.log('Infinite scroll connected to DOM');
    if (!this.initialized) {
      // Delay initialization to ensure proper DOM setup
      setTimeout(() => {
        this.init();
        this.initialized = true;
      }, 200);
    }
  }

  setupIntersectionObserver() {
    // Create a sentinel element for intersection observer
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'infinite-scroll-sentinel';
    this.sentinel.style.height = '1px';
    this.sentinel.style.width = '100%';
    this.appendChild(this.sentinel);

    // Track if we've seen the initial intersection (to avoid loading on page load)
    this.hasSeenInitialIntersection = false;

    // Set up intersection observer for automatic loading
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
        //   console.log('Intersection observer triggered:', {
        //     isIntersecting: entry.isIntersecting,
        //     loading: this.loading,
        //     currentPage: this.currentPage,
        //     totalPages: this.totalPages,
        //     hasSeenInitialIntersection: this.hasSeenInitialIntersection
        //   });
          
          // Skip the first intersection event if it happens immediately on load
          if (!this.hasSeenInitialIntersection) {
            this.hasSeenInitialIntersection = true;
            console.log('Skipping initial intersection');
            return;
          }
          
          if (entry.isIntersecting && !this.loading && this.currentPage < this.totalPages) {
            console.log('Loading more products via intersection observer');
            this.loadMoreProducts();
          }
        });
      },
      {
        root: null, // Use viewport as root
        rootMargin: '50px',
        threshold: 0
      }
    );

    // Delay observing slightly to avoid immediate triggering
    setTimeout(() => {
      this.observer.observe(this.sentinel);
      console.log('Intersection observer set up for sentinel');
    }, 100);
  }

  setupLoadMoreButton() {
    // Create load more button as fallback
    this.loadMoreButton = document.createElement('button');
    this.loadMoreButton.className = 'button button--secondary infinite-scroll-button';
    this.loadMoreButton.textContent = 'Load More Products';
    this.loadMoreButton.style.display = 'none'; // Hidden by default
    this.loadMoreButton.addEventListener('click', () => {
      console.log('Load more button clicked');
      this.loadMoreProducts();
    });
    this.appendChild(this.loadMoreButton);

    // Show button only if intersection observer is not supported
    if (!window.IntersectionObserver) {
      console.log('Intersection Observer not supported, using button fallback');
      this.loadMoreButton.style.display = 'block';
      this.sentinel.style.display = 'none';
    }
  }

  async loadMoreProducts() {
    if (this.loading || this.currentPage >= this.totalPages) {
      console.log('Load more skipped:', { loading: this.loading, currentPage: this.currentPage, totalPages: this.totalPages });
      return;
    }

    this.loading = true;
    this.showLoadingState();

    try {
      const nextPage = this.currentPage + 1;
      
      // Refresh URL params to get current filter state
      this.refreshFilterParams();
      
      const url = this.buildNextPageUrl(nextPage);
      
      console.log('Loading page:', nextPage, 'URL:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const html = await response.text();
      console.log('Received HTML length:', html.length);
      
      this.appendProducts(html);
      this.currentPage = nextPage;

    } catch (error) {
      console.error('Error loading more products:', error);
      this.showErrorState();
    } finally {
      this.loading = false;
      this.hideLoadingState();
    }
  }

  refreshFilterParams() {
    // Update URL params to current state (in case filters changed)
    this.urlParams = new URLSearchParams(window.location.search);
    console.log('Refreshed filter params:', Object.fromEntries(this.urlParams));
  }

  buildNextPageUrl(page) {
    // Always get current URL to ensure we have the latest filter/sort parameters
    const currentUrl = new URL(window.location.href);
    
    // Set the page number for pagination
    currentUrl.searchParams.set('page', page);
    
    // For Shopify section rendering, we need section_id
    currentUrl.searchParams.set('section_id', this.sectionId);
    
    const fullUrl = currentUrl.toString();
    
    console.log('Built URL with filters:', fullUrl);
    console.log('Current URL params:', Object.fromEntries(currentUrl.searchParams));
    return fullUrl;
  }

  appendProducts(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newProducts = doc.querySelectorAll('#product-grid li');
    const productGrid = document.getElementById('product-grid');

    newProducts.forEach((product, index) => {
      // Update animation order for scroll animations
      const animationOrder = productGrid.children.length + index + 1;
      if (product.hasAttribute('data-cascade')) {
        product.style.setProperty('--animation-order', animationOrder);
      }
      productGrid.appendChild(product);
    });

    // Trigger scroll animations if they exist
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }

    // Update product count if it exists
    this.updateProductCount(doc);
  }

  updateProductCount(doc) {
    const newCountElement = doc.getElementById('ProductCount');
    const newCountElementDesktop = doc.getElementById('ProductCountDesktop');
    const currentCountElement = document.getElementById('ProductCount');
    const currentCountElementDesktop = document.getElementById('ProductCountDesktop');

    if (newCountElement && currentCountElement) {
      currentCountElement.innerHTML = newCountElement.innerHTML;
    }
    if (newCountElementDesktop && currentCountElementDesktop) {
      currentCountElementDesktop.innerHTML = newCountElementDesktop.innerHTML;
    }
  }

  showLoadingState() {
    // Create loading spinner if it doesn't exist
    if (!this.loadingSpinner) {
      this.loadingSpinner = document.createElement('div');
      this.loadingSpinner.className = 'infinite-scroll-loading';
      this.loadingSpinner.innerHTML = `
        <div class="loading__spinner">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
        <span class="loading-text">Loading more products...</span>
      `;
      this.appendChild(this.loadingSpinner);
    }
    this.loadingSpinner.style.display = 'flex';
  }

  hideLoadingState() {
    if (this.loadingSpinner) {
      this.loadingSpinner.style.display = 'none';
    }

    // Hide sentinel and button if we've loaded all pages
    if (this.currentPage >= this.totalPages) {
      this.sentinel.style.display = 'none';
      this.loadMoreButton.style.display = 'none';
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  showErrorState() {
    // Show load more button as fallback on error
    this.loadMoreButton.style.display = 'block';
    this.loadMoreButton.textContent = 'Failed to load. Try again';
    setTimeout(() => {
      this.loadMoreButton.textContent = 'Load More Products';
    }, 3000);
  }
}

customElements.define('infinite-scroll', InfiniteScroll);